<template>
    <section class="people-groups-panel card">
        <div v-if="props.initialGroupId && errorMessage" class="alert alert-danger m-3" role="alert">{{ errorMessage }}</div>
        <div v-if="!props.initialGroupId" class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div class="people-groups-heading">
                <span class="people-groups-heading__icon material-icons-round" aria-hidden="true">groups</span>
                <div>
                    <h5 class="mb-1">People Groups</h5>
                    <p class="text-sm text-muted mb-0">
                        Coordinate family and friends' assignments while each member keeps control of their schedule.
                    </p>
                </div>
            </div>
            <button
                v-if="canCreateGroup"
                type="button"
                class="btn btn-sm btn-primary mb-0"
                @click="showCreateForm = !showCreateForm"
            >
                {{ showCreateForm ? "Cancel" : "Create New Group" }}
            </button>
        </div>

        <div v-if="!props.initialGroupId" class="card-body">
            <div v-if="errorMessage" class="alert alert-danger" role="alert">{{ errorMessage }}</div>
            <section
                v-if="canRespondToInvitations && (pendingInvitationsLoading || pendingInvitationsError || pendingInvitations.length)"
                class="pending-group-invitations mb-4"
                aria-labelledby="pending-group-invitations-title"
            >
                <header class="pending-group-invitations__header">
                    <span class="material-icons-round" aria-hidden="true">mark_email_unread</span>
                    <div>
                        <h6 id="pending-group-invitations-title" class="mb-1">Invitations waiting for you</h6>
                        <p class="text-sm mb-0">Review each invitation before joining a People Group.</p>
                    </div>
                </header>
                <p v-if="pendingInvitationsLoading" class="text-sm text-muted mb-0">Loading invitations...</p>
                <p v-else-if="pendingInvitationsError" class="alert alert-danger py-2 mb-0" role="alert">
                    {{ pendingInvitationsError }}
                    <button type="button" class="btn btn-sm btn-link p-0 ms-2" @click="loadPendingInvitations">Retry</button>
                </p>
                <div v-else class="pending-group-invitations__list">
                    <article v-for="invitation in pendingInvitations" :key="invitation.id" class="pending-group-invitation">
                        <div class="pending-group-invitation__details">
                            <strong>{{ invitation.group.name }}</strong>
                            <span class="group-type-badge">{{ invitation.group.people_group_type }}</span>
                            <p class="text-sm text-muted mb-2">
                                Invited by {{ invitation.invited_by || "a People Group admin" }}
                                · Expires {{ formatDate(invitation.expires_at) }}
                            </p>
                            <label class="invitation-consent-option">
                                <input v-model="invitationConsent[invitation.id]" type="checkbox">
                                <span>
                                    Allow this group's admins to manage my schedule and availability.
                                    <small class="d-block text-muted">Optional. You can change this choice later.</small>
                                </span>
                            </label>
                        </div>
                        <div class="pending-group-invitation__actions">
                            <button
                                type="button"
                                class="btn btn-sm btn-primary mb-0"
                                :disabled="respondingInvitationId !== null"
                                @click="respondToInvitation(invitation, 'accept')"
                            >
                                {{ respondingInvitationId === invitation.id ? "Joining..." : "Accept" }}
                            </button>
                            <button
                                type="button"
                                class="btn btn-sm btn-outline-secondary mb-0"
                                :disabled="respondingInvitationId !== null"
                                @click="respondToInvitation(invitation, 'decline')"
                            >
                                {{ respondingInvitationId === invitation.id ? "Updating..." : "Decline" }}
                            </button>
                        </div>
                    </article>
                </div>
                <p v-if="invitationActionError" class="alert alert-danger py-2 mt-3 mb-0" role="alert">
                    {{ invitationActionError }}
                </p>
            </section>
            <div v-if="showCreateForm" class="create-group-form mb-4">
                <label class="form-label" for="people-group-name">Group name</label>
                <input id="people-group-name" v-model.trim="newGroupName" class="form-control mb-2" maxlength="100">
                <label class="form-label" for="people-group-type">Group type</label>
                <select id="people-group-type" v-model="newGroupType" class="form-select mb-3">
                    <option value="family">Family</option>
                    <option value="friends">Friends</option>
                    <option value="other">Other</option>
                </select>
                <button type="button" class="btn btn-primary btn-sm mb-0" :disabled="saving || !newGroupName" @click="createGroup">
                    {{ saving ? "Saving..." : "Create Group" }}
                </button>
            </div>

            <section v-if="props.showAdminReport && isLocationAdmin" class="admin-people-groups-report mb-4">
                <div class="d-flex justify-content-between align-items-center gap-2 mb-2">
                    <div>
                        <h6 class="mb-1">Location People Groups report</h6>
                        <p class="text-sm text-muted mb-0">A quick count of groups and volunteers connected to at least one group.</p>
                    </div>
                    <button type="button" class="btn btn-sm btn-outline-primary mb-0" :disabled="adminReportLoading" @click="loadAdminReport">
                        {{ adminReportLoading ? "Refreshing..." : "Refresh" }}
                    </button>
                </div>
                <p v-if="adminReportError" class="alert alert-danger py-2 mb-2" role="alert">{{ adminReportError }}</p>
                <p v-else-if="adminReportLoading && !adminReport" class="text-sm text-muted mb-2">Loading People Groups report...</p>
                <div v-else-if="adminReport" class="admin-report-stats">
                    <article class="admin-report-stat">
                        <span>People Groups</span>
                        <strong>{{ adminReport.people_group_count }}</strong>
                    </article>
                    <article class="admin-report-stat">
                        <span>Volunteers in at least one group</span>
                        <strong>{{ adminReport.volunteer_count }}</strong>
                    </article>
                </div>

                <details
                    class="group-section group-section--audit admin-global-audit mt-3"
                    :open="adminAuditExpanded"
                    @toggle="handleAdminAuditToggle"
                >
                    <summary class="d-flex justify-content-between align-items-center gap-2">
                        <h6 class="mb-0">People Group Audit Events</h6>
                        <span v-if="adminAuditLoaded" class="text-sm text-muted">{{ adminAuditTotal }} entries</span>
                        <span v-else-if="adminAuditLoading" class="text-sm text-muted">Loading...</span>
                        <span v-else class="text-sm text-muted">Expand to view</span>
                    </summary>
                    <div class="mt-3">
                        <p v-if="adminAuditError" class="alert alert-danger py-2 mb-2" role="alert">
                            {{ adminAuditError }}
                            <button type="button" class="btn btn-sm btn-link p-0 ms-2" @click="loadAdminAuditLogs(adminAuditPage)">Retry</button>
                        </p>
                        <p v-if="adminAuditLoading" class="text-sm text-muted mb-2">Loading Location audit events...</p>
                        <p v-else-if="adminAuditLoaded && !adminAuditError && adminAuditLogs.length === 0" class="text-sm text-muted mb-2">
                            No People Group audit events are available for this Location.
                        </p>
                        <ul v-else-if="adminAuditLogs.length" class="list-group mb-2">
                            <li v-for="entry in adminAuditLogs" :key="entry.id" class="list-group-item">
                                <div class="d-flex justify-content-between align-items-start gap-2">
                                    <div>
                                        <strong>{{ formatAuditAction(entry.action_type) }}</strong>
                                        <button type="button" class="btn btn-link btn-sm p-0 ms-2" @click="openGroup({ id: entry.people_group_id })">
                                            {{ entry.people_group_name || `Group #${entry.people_group_id}` }}
                                        </button>
                                    </div>
                                    <span class="text-sm text-muted text-nowrap">{{ formatDate(entry.created_at) }}</span>
                                </div>
                                <span class="d-block text-sm text-muted">
                                    By {{ entry.action_by_name || `Person #${entry.action_by_user_id}` }}
                                    <template v-if="entry.target_user_id">
                                        · For {{ entry.target_user_name || `Person #${entry.target_user_id}` }}
                                    </template>
                                </span>
                                <details v-if="entry.details">
                                    <summary class="text-sm">View details</summary>
                                    <pre class="audit-details mb-0">{{ formatAuditDetails(entry.details) }}</pre>
                                </details>
                            </li>
                        </ul>
                        <nav v-if="adminAuditLastPage > 1" class="d-flex align-items-center justify-content-between" aria-label="Location People Group audit pagination">
                            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="adminAuditLoading || adminAuditPage <= 1" @click="loadAdminAuditLogs(adminAuditPage - 1)">
                                Previous
                            </button>
                            <span class="text-sm text-muted">Page {{ adminAuditPage }} of {{ adminAuditLastPage }}</span>
                            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="adminAuditLoading || adminAuditPage >= adminAuditLastPage" @click="loadAdminAuditLogs(adminAuditPage + 1)">
                                Next
                            </button>
                        </nav>
                    </div>
                </details>
            </section>

            <div class="mb-3">
                <label class="form-label" for="people-group-search">Search People Groups</label>
                <input
                    id="people-group-search"
                    v-model="groupSearch"
                    class="form-control"
                    type="search"
                    placeholder="Search by group name"
                    maxlength="100"
                    @input="scheduleGroupSearch"
                >
            </div>
            <div v-if="loading" class="text-muted">Loading People Groups...</div>
            <div v-else-if="groups.length === 0" class="text-muted">
                {{ groupSearch.trim() ? "No People Groups match your search." : "You are not in any People Groups yet." }}
            </div>
            <div v-else class="people-group-list">
                <article v-for="group in groups" :key="group.id" class="people-group-row">
                    <div class="people-group-row__summary">
                        <span class="people-group-row__icon material-icons-round" aria-hidden="true">group</span>
                        <div>
                            <strong>{{ group.name }}</strong>
                            <div class="people-group-row__meta">
                                <span class="group-type-badge">{{ group.people_group_type }}</span>
                                <span>{{ group.member_count }} of {{ group.max_members }} members</span>
                            </div>
                        </div>
                    </div>
                    <div class="d-flex align-items-center gap-2">
                        <button type="button" class="btn btn-sm btn-outline-primary mb-0" @click="openGroup(group)">
                            View
                        </button>
                        <button
                            v-if="targetUserId && group.member_ids.includes(Number(targetUserId))"
                            type="button"
                            class="btn btn-sm btn-outline-danger mb-0"
                            @click="toggleTargetMembership(group)"
                        >
                            Remove
                        </button>
                    </div>
                </article>
            </div>
            <nav v-if="groupLastPage > 1" class="d-flex align-items-center justify-content-between mt-3" aria-label="People Groups pagination">
                <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="loading || groupCurrentPage <= 1" @click="loadGroups(groupCurrentPage - 1)">
                    Previous
                </button>
                <span class="text-sm text-muted">Page {{ groupCurrentPage }} of {{ groupLastPage }} · {{ groupTotal }} groups</span>
                <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="loading || groupCurrentPage >= groupLastPage" @click="loadGroups(groupCurrentPage + 1)">
                    Next
                </button>
            </nav>
        </div>

        <div v-if="selectedGroup" class="group-modal-backdrop" role="presentation" @click.self="closeGroupModal">
            <section class="group-modal" role="dialog" aria-modal="true" :aria-label="`${selectedGroup.name} details`">
                <header class="d-flex justify-content-between align-items-start gap-3 mb-3">
                    <div>
                        <h5 class="mb-1">{{ selectedGroup.name }}</h5>
                        <p class="text-sm text-muted mb-0">{{ selectedGroup.people_group_type }} · {{ selectedGroup.member_count }} of {{ selectedGroup.max_members }} members</p>
                    </div>
                    <button type="button" class="people-group-modal-close" aria-label="Close People Group details" @click="closeGroupModal">×</button>
                </header>

                <details v-if="canManageSelectedGroup" class="group-section group-section--settings group-disclosure mb-3">
                    <summary>
                        <span class="disclosure-title">Group Settings</span>
                        <span class="disclosure-description">Update the group name and type</span>
                    </summary>
                    <div class="mt-3">
                        <label class="form-label" for="edit-people-group-name">Group name</label>
                        <input id="edit-people-group-name" v-model.trim="editGroupName" class="form-control mb-2" maxlength="100">
                        <label class="form-label" for="edit-people-group-type">Group type</label>
                        <select id="edit-people-group-type" v-model="editGroupType" class="form-select mb-2">
                            <option value="family">Family</option>
                            <option value="friends">Friends</option>
                            <option value="other">Other</option>
                        </select>
                        <button type="button" class="btn btn-sm btn-outline-primary mb-0" :disabled="saving || !editGroupName" @click="saveGroupDetails">
                            Save Changes
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-danger mb-0 ms-2" :disabled="saving || !canDeleteSelectedGroup" @click="deleteGroup">
                            Delete Group
                        </button>
                        <small v-if="!canDeleteSelectedGroup" class="d-block text-sm text-muted mt-2">
                            Remove all members except one group admin before deleting this group.
                        </small>
                    </div>
                </details>

                <details v-if="selectedGroup.members?.some(member => member.user_id === currentUserId)" class="consent-panel group-section group-section--consent group-disclosure mb-3">
                    <summary>
                        <span class="disclosure-title">My Group Settings</span>
                        <span class="consent-status" :class="{ 'consent-status--enabled': currentMemberConsent }">
                            {{ currentMemberConsent ? "Schedule access on" : "Schedule access off" }}
                        </span>
                    </summary>
                    <div class="mt-3">
                        <label class="group-consent-option">
                            <input
                                class="group-consent-checkbox"
                                type="checkbox"
                                :checked="currentMemberConsent"
                                :disabled="saving"
                                @change="updateConsent"
                            >
                            <span>
                                <strong>Allow group admins to manage my schedule and availability</strong>
                                <small class="d-block text-muted">Includes availability, scheduling rules, and preferred activities and workgroups. This permission applies only to this group and can be changed at any time.</small>
                            </span>
                        </label>
                    </div>
                </details>

                <section class="group-section group-section--snapshot mb-3">
                    <div class="d-flex justify-content-between align-items-start gap-2 mb-3">
                        <div>
                            <h6 class="mb-1">Group at a glance</h6>
                            <p class="text-sm text-muted mb-0">Future non-declined assignments across members. Shared counts require at least two distinct members.</p>
                        </div>
                    </div>
                    <div class="group-stat-grid">
                        <article class="group-stat-card">
                            <span>Upcoming assignments</span>
                            <strong>{{ groupSnapshot.upcomingAssignments }}</strong>
                        </article>
                        <article class="group-stat-card group-stat-card--confirmed">
                            <span>Confirmed</span>
                            <strong>{{ groupSnapshot.confirmedAssignments }}</strong>
                        </article>
                        <article class="group-stat-card group-stat-card--pending">
                            <span>Unconfirmed</span>
                            <strong>{{ groupSnapshot.unconfirmedAssignments }}</strong>
                        </article>
                        <article class="group-stat-card group-stat-card--swaps">
                            <span>Pending swap requests</span>
                            <strong>{{ groupSnapshot.pendingSwapRequests }}</strong>
                        </article>
                        <article class="group-stat-card group-stat-card--shared">
                            <span>Shared Activity Instances</span>
                            <strong>{{ groupSnapshot.sharedActivityInstances }}</strong>
                        </article>
                        <article class="group-stat-card group-stat-card--shared">
                            <span>Shared Activity Workgroups</span>
                            <strong>{{ groupSnapshot.sharedWorkgroups }}</strong>
                        </article>
                    </div>
                </section>

                <section class="group-section group-section--members mb-3">
                    <div class="d-flex justify-content-between align-items-center gap-2">
                        <h6 class="mb-0">Members</h6>
                        <button
                            v-if="canManageSelectedGroup"
                            type="button"
                            class="btn btn-sm btn-outline-primary mb-0"
                            :aria-expanded="showAddMemberSection"
                            @click="toggleAddMemberSection"
                        >
                            {{ showAddMemberSection ? "Close Invitations" : "Invite Location Members" }}
                        </button>
                    </div>
                    <p v-if="isLocationAdmin" class="text-sm text-muted mt-2">
                        Availability, scheduling rules, and activity/workgroup preferences are edited per member. Each member's own skills and permissions determine which activities and workgroups are available.
                    </p>
                <div v-if="canManageSelectedGroup && showAddMemberSection" class="group-section group-section--add-member mt-3 mb-3">
                    <label class="form-label" for="people-group-member-search">Invite an existing Location member</label>
                    <input
                        id="people-group-member-search"
                        v-model="memberSearch"
                        class="form-control mb-2"
                        type="search"
                        :placeholder="isLocationAdmin ? 'Search name, email, or mobile number' : 'Search by first or last name'"
                        @input="scheduleAvailableSearch"
                    >
                    <p v-if="!isLocationAdmin && memberSearch.trim().length < 2" class="text-sm text-muted mb-2">
                        Enter at least 2 characters to search by first or last name.
                    </p>
                    <div v-if="loadingAvailableMembers" class="text-sm text-muted">Searching Location members...</div>
                    <div v-else-if="availableMembers.length" class="available-member-list">
                        <div v-for="candidate in availableMembers" :key="candidate.user_id" class="available-member-row">
                            <span>
                                <strong>{{ candidate.name }}</strong>
                                <small v-if="isLocationAdmin" class="d-block text-muted">
                                    {{ candidate.email }}<span v-if="candidate.mobilephone"> · {{ candidate.mobilephone }}</span>
                                </small>
                                <small v-else-if="!candidate.can_invite" class="d-block text-muted">
                                    This member does not have a valid email address.
                                </small>
                            </span>
                            <button type="button" class="btn btn-sm btn-outline-primary mb-0" :disabled="saving || !candidate.can_invite" @click="inviteExistingMember(candidate)">
                                Invite
                            </button>
                        </div>
                    </div>
                    <p v-else-if="isLocationAdmin || memberSearch.trim().length >= 2" class="text-sm text-muted mb-0">
                        No eligible Location members found.
                    </p>
                    <nav v-if="availableLastPage > 1" class="d-flex align-items-center justify-content-between mt-2" aria-label="Available members pagination">
                        <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="loadingAvailableMembers || availablePage <= 1" @click="loadAvailableMembers(availablePage - 1)">
                            Previous
                        </button>
                        <span class="text-sm text-muted">Page {{ availablePage }} of {{ availableLastPage }} · {{ availableTotal }} users</span>
                        <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="loadingAvailableMembers || availablePage >= availableLastPage" @click="loadAvailableMembers(availablePage + 1)">
                            Next
                        </button>
                    </nav>
                </div>

                <ul class="list-group mt-3 mb-0">
                    <li v-for="member in selectedGroup.members" :key="member.user_id" class="list-group-item member-detail-row group-section--member">
                        <div class="d-flex flex-column flex-xl-row justify-content-between gap-2">
                            <div>
                                <strong>{{ member.name || member.email || `Person #${member.user_id}` }}</strong>
                                <span class="d-block text-sm text-muted">
                                    {{ member.is_group_admin ? "Group admin" : "Member" }}
                                    · {{ member.allow_admin_management ? "Schedule management allowed" : "Schedule management not allowed" }}
                                    · {{ member.upcoming_assignment_count }} upcoming assignments
                                    · {{ member.pending_swap_request_count }} pending swap requests
                                </span>
                            </div>
                            <div class="d-flex align-items-center flex-wrap gap-2">
                                <button
                                    v-if="canManageSelectedGroup"
                                    type="button"
                                    class="btn btn-sm btn-outline-primary mb-0"
                                    :disabled="saving || (member.is_group_admin && isLastGroupAdmin(member))"
                                    :title="groupAdminActionTitle(member)"
                                    @click="toggleGroupAdmin(member)"
                                >
                                    {{ member.is_group_admin ? "Remove Group Admin" : "Make Group Admin" }}
                                </button>
                                <button
                                    v-if="Number(member.user_id) === currentUserId || canManageSelectedGroup"
                                    type="button"
                                    class="btn btn-sm btn-outline-danger mb-0"
                                    :disabled="saving || isLastGroupAdmin(member)"
                                    :title="isLastGroupAdmin(member) ? 'Promote another member to group admin before leaving or removing this member.' : Number(member.user_id) === currentUserId ? 'Leave this People Group' : 'Remove this member from the People Group'"
                                    @click="removeGroupMember(member)"
                                >
                                    {{ Number(member.user_id) === currentUserId ? "Leave Group" : "Remove Member" }}
                                </button>
                                <small
                                    v-if="isLastGroupAdmin(member) && (Number(member.user_id) === currentUserId || canManageSelectedGroup)"
                                    class="text-sm text-muted"
                                >
                                    Promote another member to group admin before leaving or removing this member.
                                </small>
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-primary mb-0"
                                    title="Open this member's schedule"
                                    @click="openMemberSchedule(member)"
                                >
                                    Open Schedule
                                </button>
                                <button
                                    v-if="isLocationAdmin"
                                    type="button"
                                    class="btn btn-sm btn-outline-primary mb-0"
                                    title="Open this member's profile and individual scheduling settings"
                                    @click="editMemberProfile(member, 'user-details')"
                                >
                                    Edit Profile
                                </button>
                                <button
                                    v-if="canEditMemberScheduleSettings(member)"
                                    type="button"
                                    class="btn btn-sm btn-outline-primary mb-0"
                                    title="Edit this member's availability"
                                    @click="editMemberProfile(member, 'availability')"
                                >
                                    Edit Availability
                                </button>
                                <button
                                    v-if="canEditMemberScheduleSettings(member)"
                                    type="button"
                                    class="btn btn-sm btn-outline-primary mb-0"
                                    title="Edit this member's preferred activities and workgroups"
                                    @click="editMemberProfile(member, 'preferred-activities')"
                                >
                                    Edit Activities &amp; Workgroups
                                </button>
                                <button
                                    v-if="canEditMemberScheduleSettings(member)"
                                    type="button"
                                    class="btn btn-sm btn-outline-primary mb-0"
                                    title="Edit scheduling rules for this member"
                                    @click="editMemberRules(member)"
                                >
                                    Edit Rules
                                </button>
                            </div>
                        </div>
                        <details
                            v-if="canViewMemberScheduling(member)"
                            class="group-disclosure group-section group-section--member-overview mt-2"
                            @toggle="handleMemberOverviewToggle(member, $event)"
                        >
                            <summary class="d-flex justify-content-between align-items-center gap-2">
                                <span class="disclosure-title">Availability, rules &amp; serving preferences</span>
                                <span v-if="member.scheduling_overview" class="text-sm text-muted">
                                    {{ member.scheduling_overview.future_availability.length }} availability ·
                                    {{ member.scheduling_overview.scheduling_rules.length }} rules
                                </span>
                                <span v-else class="text-sm text-muted">Expand to load</span>
                            </summary>
                            <div class="mt-3">
                                <p v-if="member.scheduling_overview_loading" class="text-sm text-muted">Loading this member's scheduling details...</p>
                                <p v-else-if="member.scheduling_overview_error" class="alert alert-danger py-2" role="alert">
                                    {{ member.scheduling_overview_error }}
                                    <button type="button" class="btn btn-sm btn-link p-0 ms-2" @click="loadMemberSchedulingOverview(member, true)">Retry</button>
                                </p>
                                <div v-else-if="member.scheduling_overview" class="member-overview-grid">
                                    <section class="member-overview-card">
                                        <h6>Future availability</h6>
                                        <ul v-if="member.scheduling_overview.future_availability.length">
                                            <li v-for="(item, index) in member.scheduling_overview.future_availability" :key="`availability-${index}`">{{ item }}</li>
                                        </ul>
                                        <p v-else class="mb-0 text-sm text-muted">No future availability rules are recorded.</p>
                                    </section>
                                    <section class="member-overview-card">
                                        <h6>Scheduling rules</h6>
                                        <ul v-if="member.scheduling_overview.scheduling_rules.length">
                                            <li v-for="rule in member.scheduling_overview.scheduling_rules" :key="rule.ruleid">{{ rule.description }}</li>
                                        </ul>
                                        <p v-else class="mb-0 text-sm text-muted">No scheduling rules are recorded.</p>
                                    </section>
                                    <section class="member-overview-card">
                                        <h6>Preferred activities</h6>
                                        <p v-if="member.scheduling_overview.interested_in_any_activity" class="mb-0 text-sm">Open to any eligible activity.</p>
                                        <ul v-else-if="member.scheduling_overview.preferred_activities.length">
                                            <li v-for="activity in member.scheduling_overview.preferred_activities" :key="activity.id">{{ activity.name }}</li>
                                        </ul>
                                        <p v-else class="mb-0 text-sm text-muted">No activity preferences are recorded.</p>
                                    </section>
                                    <section class="member-overview-card">
                                        <h6>Preferred workgroups</h6>
                                        <p v-if="member.scheduling_overview.interested_in_any_workgroup" class="mb-0 text-sm">Open to any eligible workgroup.</p>
                                        <ul v-else-if="member.scheduling_overview.preferred_workgroups.length">
                                            <li v-for="workgroup in member.scheduling_overview.preferred_workgroups" :key="workgroup.id">
                                                {{ workgroup.activity_name }} · {{ workgroup.skill_name || workgroup.name }}
                                            </li>
                                        </ul>
                                        <p v-else class="mb-0 text-sm text-muted">No workgroup preferences are recorded.</p>
                                    </section>
                                    <section class="member-overview-card">
                                        <h6>Location skills</h6>
                                        <ul v-if="member.scheduling_overview.skills.length">
                                            <li v-for="skill in member.scheduling_overview.skills" :key="skill.id">{{ skill.name }}</li>
                                        </ul>
                                        <p v-else class="mb-0 text-sm text-muted">No Location skills are assigned.</p>
                                    </section>
                                </div>
                            </div>
                        </details>
                        <p
                            v-else-if="canManageSelectedGroup && !isLocationAdmin && member.user_id !== currentUserId && !member.allow_admin_management"
                            class="member-consent-notice mt-2 mb-0 text-sm"
                        >
                            This member has not shared scheduling settings with group admins.
                        </p>
                        <details v-if="member.upcoming_assignments?.length" class="member-assignments group-section group-section--assignments mt-2">
                            <summary>View upcoming assignments</summary>
                            <ul class="list-group list-group-flush mt-2">
                                <li v-for="assignment in member.upcoming_assignments" :key="`${assignment.activity_instance_id}-${assignment.ai_workgroup_id}`" class="list-group-item px-0 py-2">
                                    <strong>{{ assignment.activity_name }} · {{ assignment.workgroup_name }}</strong>
                                    <span class="d-block text-sm text-muted">{{ formatDate(assignment.start_datetime) }}</span>
                                    <span class="badge" :class="assignment.status === 'confirmed' ? 'bg-success' : 'bg-warning text-dark'">
                                        {{ assignment.status === 'confirmed' ? "Confirmed" : "Pending" }}
                                    </span>
                                    <div v-if="canManageScheduleForMember(member)" class="d-flex flex-wrap gap-2 mt-2">
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-outline-success mb-0"
                                            :disabled="saving || assignment.status_id === 1"
                                            :title="`Confirm this assignment on behalf of ${member.name}`"
                                            @click="respondToAssignment(member, assignment, 1)"
                                        >
                                            Confirm on behalf
                                        </button>
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-outline-danger mb-0"
                                            :disabled="saving"
                                            :title="`Decline this assignment on behalf of ${member.name}`"
                                            @click="respondToAssignment(member, assignment, 2)"
                                        >
                                            Decline on behalf
                                        </button>
                                    </div>
                                </li>
                            </ul>
                        </details>
                        <details v-if="member.pending_swap_requests?.length" class="member-swaps group-section group-section--swaps mt-2">
                            <summary>View pending swap requests</summary>
                            <ul class="list-group list-group-flush mt-2">
                                <li v-for="(swap, index) in member.pending_swap_requests" :key="`${member.user_id}-swap-${index}`" class="list-group-item px-0 py-2">
                                    {{ swap.role === "requestor" ? "Requested a swap with" : "Received a swap request from" }}
                                    <strong>{{ swap.counterparty_name }}</strong>
                                    <span v-if="swap.workgroup_name"> · {{ swap.workgroup_name }}</span>
                                    <span v-if="swap.start_datetime"> · {{ formatDate(swap.start_datetime) }}</span>
                                    <span v-if="swap.request_date"> · Requested {{ formatDate(swap.request_date) }}</span>
                                    <span class="d-block text-sm text-muted">
                                        Request assignment: {{ formatDate(swap.request_assignment_start_datetime) }}
                                        · Response assignment: {{ formatDate(swap.response_assignment_start_datetime) }}
                                    </span>
                                    <div v-if="swap.role === 'responder' && canRespondForMember(member)" class="d-flex gap-2 mt-2">
                                        <button type="button" class="btn btn-sm btn-outline-success mb-0" :disabled="saving" @click="respondToSwap(member, swap, 1)">
                                            Accept
                                        </button>
                                        <button type="button" class="btn btn-sm btn-outline-danger mb-0" :disabled="saving" @click="respondToSwap(member, swap, 2)">
                                            Decline
                                        </button>
                                    </div>
                                </li>
                            </ul>
                        </details>
                    </li>
                </ul>
                </section>

                <section v-if="canManageSelectedGroup" class="group-section group-section--invitations mb-3">
                    <h6 class="mb-2">Pending Invitations</h6>
                    <p v-if="!selectedGroup.pending_invitations?.length" class="text-sm text-muted mb-0">
                        No invitations are waiting for acceptance.
                    </p>
                    <ul v-else class="list-group mb-0">
                        <li
                            v-for="invitation in selectedGroup.pending_invitations"
                            :key="invitation.id"
                            class="list-group-item d-flex flex-column flex-sm-row justify-content-between align-items-start gap-2"
                        >
                            <span>
                                <strong>{{ invitationRecipientLabel(invitation) }}</strong>
                                <small class="d-block text-muted">
                                    Invited {{ formatDate(invitation.created_at) }}
                                    · Expires {{ formatDate(invitation.expires_at) }}
                                </small>
                            </span>
                            <button
                                type="button"
                                class="btn btn-sm btn-outline-danger mb-0"
                                :disabled="saving"
                                @click="cancelInvitation(invitation)"
                            >
                                Cancel Invitation
                            </button>
                        </li>
                    </ul>
                </section>

                <div v-if="selectedRuleMember" class="group-section group-section--rules mb-3">
                    <div class="nested-modal-backdrop" role="presentation" @click.self="closeMemberRules">
                        <section class="nested-profile-modal rules-modal" role="dialog" aria-modal="true" :aria-label="`${selectedRuleMember.name} scheduling rules`">
                            <header class="nested-modal-header d-flex justify-content-between align-items-center gap-3 mb-3">
                                <h5 class="mb-0">{{ selectedRuleMember.name }} — Scheduling Rules</h5>
                                <button type="button" class="people-group-modal-close" aria-label="Close scheduling rules" @click="closeMemberRules">×</button>
                            </header>
                            <SchedulePreferences
                                :key="selectedRuleMember.user_id"
                                :target-person-id="selectedRuleMember.user_id"
                                :group-admin-edit="true"
                                :people-group-id="selectedGroup.id"
                            />
                            <footer class="nested-modal-footer d-flex justify-content-end mt-3 pt-3 border-top">
                                <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="closeMemberRules">Close Rules</button>
                            </footer>
                        </section>
                    </div>
                </div>

                <div v-if="selectedGroup.shared_assignments?.length" class="group-section group-section--shared mb-3">
                    <h6>Serving together</h6>
                    <ul class="list-group mb-3">
                        <li v-for="(assignment, index) in selectedGroup.shared_assignments" :key="`${assignment.activity_instance_id}-${index}`" class="list-group-item">
                            <strong>{{ assignment.activity_name }} · {{ assignment.workgroup_name }}</strong>
                            <span class="d-block text-muted text-sm">{{ formatDate(assignment.start_datetime) }} — {{ assignment.member_names.join(", ") }}</span>
                        </li>
                    </ul>
                </div>
                <p v-else class="text-muted text-sm">No upcoming assignments are currently shared by group members.</p>

                <details
                    v-if="canManageSelectedGroup"
                    class="group-section group-section--audit mb-3"
                    :open="auditExpanded"
                    @toggle="handleAuditToggle"
                >
                    <summary class="d-flex justify-content-between align-items-center gap-2 mb-2">
                        <h6 class="mb-0">Audit History</h6>
                        <span v-if="auditLoaded" class="text-sm text-muted">{{ auditTotal }} entries</span>
                        <span v-else-if="auditLoading" class="text-sm text-muted">Loading...</span>
                        <span v-else class="text-sm text-muted">Expand to view</span>
                    </summary>
                    <div>
                        <p v-if="auditError" class="alert alert-danger py-2 mb-2" role="alert">
                            {{ auditError }}
                            <button type="button" class="btn btn-sm btn-link p-0 ms-2" @click="loadAuditLogs(auditPage)">Retry</button>
                        </p>
                        <p v-if="auditLoading" class="text-sm text-muted mb-2">Loading audit history...</p>
                        <p v-else-if="auditLoaded && !auditError && auditLogs.length === 0" class="text-sm text-muted mb-2">No audit history is available for this group.</p>
                        <ul v-else-if="auditLogs.length" class="list-group mb-2">
                            <li v-for="entry in auditLogs" :key="entry.id" class="list-group-item">
                                <strong>{{ formatAuditAction(entry.action_type) }}</strong>
                                <span class="d-block text-sm text-muted">
                                    By {{ entry.action_by_name || `Person #${entry.action_by_user_id}` }}
                                    <template v-if="entry.target_user_id">
                                        · For {{ entry.target_user_name || `Person #${entry.target_user_id}` }}
                                    </template>
                                    · {{ formatDate(entry.created_at) }}
                                </span>
                                <details v-if="entry.details">
                                    <summary class="text-sm">View details</summary>
                                    <pre class="audit-details mb-0">{{ formatAuditDetails(entry.details) }}</pre>
                                </details>
                            </li>
                        </ul>
                        <nav v-if="auditLastPage > 1" class="d-flex align-items-center justify-content-between" aria-label="Audit history pagination">
                            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="auditLoading || auditPage <= 1" @click="loadAuditLogs(auditPage - 1)">
                                Previous
                            </button>
                            <span class="text-sm text-muted">Page {{ auditPage }} of {{ auditLastPage }}</span>
                            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="auditLoading || auditPage >= auditLastPage" @click="loadAuditLogs(auditPage + 1)">
                                Next
                            </button>
                        </nav>
                    </div>
                </details>

                <div class="d-flex justify-content-end border-top pt-3 mt-3">
                    <button type="button" class="btn btn-outline-secondary btn-sm mb-0" @click="closeGroupModal">Close</button>
                </div>
            </section>
        </div>

        <div v-if="selectedProfileMember" class="nested-modal-backdrop" role="presentation" @click.self="closeMemberProfile">
            <section class="nested-profile-modal" role="dialog" aria-modal="true" :aria-label="`${selectedProfileMember.name || 'Member'} profile`">
                <header class="nested-modal-header d-flex justify-content-between align-items-center gap-3 mb-3">
                    <h5 class="mb-0">{{ selectedProfileMember.name || "Member Profile" }}</h5>
                    <button type="button" class="people-group-modal-close" aria-label="Close member profile" @click="closeMemberProfile">×</button>
                </header>
                <EditUsersModal
                    :key="`${selectedProfileMember.user_id}-${selectedProfileTab}-${selectedGroup.id}`"
                    :person-id="selectedProfileMember.user_id"
                    :initial-active-tab="selectedProfileTab"
                    :schedule-management-only="!isLocationAdmin"
                    :people-group-id="selectedGroup.id"
                    @account-updated="handleMemberProfileUpdated"
                />
                <footer class="nested-modal-footer d-flex justify-content-end mt-3 pt-3 border-top">
                    <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="closeMemberProfile">Close Profile</button>
                </footer>
            </section>
        </div>

        <div v-if="selectedScheduleMember" class="nested-modal-backdrop schedule-modal-backdrop" role="presentation" @click.self="closeMemberSchedule">
            <section class="nested-profile-modal schedule-modal" role="dialog" aria-modal="true" :aria-label="`${selectedScheduleMember.name || 'Member'} schedule`">
                <header class="nested-modal-header d-flex justify-content-between align-items-center gap-3 mb-3">
                    <h5 class="mb-0">{{ selectedScheduleMember.name || "Member Schedule" }}</h5>
                    <button type="button" class="people-group-modal-close" aria-label="Close member schedule" @click="closeMemberSchedule">×</button>
                </header>
                <iframe
                    class="schedule-frame"
                    :src="memberScheduleUrl(selectedScheduleMember)"
                    :title="`${selectedScheduleMember.name || 'Member'} schedule`"
                ></iframe>
                <footer class="nested-modal-footer d-flex justify-content-end mt-3 pt-3 border-top">
                    <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="closeMemberSchedule">Close Schedule</button>
                </footer>
            </section>
        </div>
    </section>
</template>

<script setup>
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useToast } from "vue-toast-notification";
import api from "@/config/api";
import { baseUrl } from "@/utils/BaseUrl.js";
import { getLocationId, getLogginedPersonId } from "@/utils/SessionDataHandler";
import SchedulePreferences from "@/modules/users/components/SchedulePreferences.vue";

const EditUsersModal = defineAsyncComponent(() => import("@/modules/users/EditUsers.vue"));

const props = defineProps({
    targetUserId: { type: [Number, String], default: null },
    showAdminReport: { type: Boolean, default: false },
    // When set, the panel opens only this group's details (list and invitations are skipped)
    initialGroupId: { type: [Number, String], default: null },
});
const emit = defineEmits(["group-closed"]);

const toast = useToast();
const locationId = getLocationId();
const currentUserId = Number(getLogginedPersonId());
const groups = ref([]);
const groupSearch = ref("");
const groupCurrentPage = ref(1);
const groupLastPage = ref(1);
const groupTotal = ref(0);
const pendingInvitations = ref([]);
const pendingInvitationsLoading = ref(false);
const pendingInvitationsError = ref("");
const invitationActionError = ref("");
const invitationConsent = ref({});
const respondingInvitationId = ref(null);
const selectedGroup = ref(null);
const selectedRuleMember = ref(null);
const selectedProfileMember = ref(null);
const selectedProfileTab = ref("user-details");
const selectedScheduleMember = ref(null);
const loading = ref(true);
const saving = ref(false);
const showCreateForm = ref(false);
const showAddMemberSection = ref(false);
const newGroupName = ref("");
const newGroupType = ref("family");
const editGroupName = ref("");
const editGroupType = ref("family");
const memberSearch = ref("");
const availableMembers = ref([]);
const loadingAvailableMembers = ref(false);
const availablePage = ref(1);
const availableLastPage = ref(1);
const availableTotal = ref(0);
const auditLogs = ref([]);
const auditLoading = ref(false);
const auditLoaded = ref(false);
const auditExpanded = ref(false);
const auditError = ref("");
const auditPage = ref(1);
const auditLastPage = ref(1);
const auditTotal = ref(0);
const adminReport = ref(null);
const adminReportLoading = ref(false);
const adminReportError = ref("");
const adminAuditLogs = ref([]);
const adminAuditLoading = ref(false);
const adminAuditLoaded = ref(false);
const adminAuditExpanded = ref(false);
const adminAuditError = ref("");
const adminAuditPage = ref(1);
const adminAuditLastPage = ref(1);
const adminAuditTotal = ref(0);
const errorMessage = ref("");
const allowVolunteerGroupCreation = ref(false);
const isLocationAdmin = ref(false);

const canCreateGroup = computed(() => isLocationAdmin.value || allowVolunteerGroupCreation.value);
const canRespondToInvitations = computed(() =>
    currentUserId > 0
    && Number.isSafeInteger(Number(locationId))
    && Number(locationId) > 0
    && (!props.targetUserId || Number(props.targetUserId) === currentUserId)
);
const currentMemberConsent = computed(() =>
    selectedGroup.value?.members?.find(member => member.user_id === currentUserId)?.allow_admin_management ?? false
);
const groupAdminCount = computed(() =>
    (selectedGroup.value?.members || []).filter(member => member.is_group_admin).length
);
const groupSnapshot = computed(() => {
    const assignments = new Map();
    const activityMembers = new Map();
    const workgroupMembers = new Map();
    const swapIds = new Set();

    for (const member of selectedGroup.value?.members || []) {
        const memberId = Number(member.user_id);
        for (const assignment of member.upcoming_assignments || []) {
            const instanceId = Number(assignment.activity_instance_id);
            const workgroupId = Number(assignment.ai_workgroup_id);
            if (!instanceId || !workgroupId) continue;

            const assignmentKey = `${memberId}:${instanceId}:${workgroupId}`;
            if (!assignments.has(assignmentKey)) {
                assignments.set(assignmentKey, Number(assignment.status_id) === 1 || assignment.status === "confirmed");
            }

            if (!activityMembers.has(instanceId)) activityMembers.set(instanceId, new Set());
            activityMembers.get(instanceId).add(memberId);

            const workgroupKey = `${instanceId}:${workgroupId}`;
            if (!workgroupMembers.has(workgroupKey)) workgroupMembers.set(workgroupKey, new Set());
            workgroupMembers.get(workgroupKey).add(memberId);
        }

        (member.pending_swap_requests || []).forEach((swap, index) => {
            swapIds.add(swap.swap_id != null ? String(swap.swap_id) : `${memberId}:${index}:${swap.role || ""}`);
        });
    }

    let confirmedAssignments = 0;
    for (const confirmed of assignments.values()) {
        if (confirmed) confirmedAssignments++;
    }

    return {
        upcomingAssignments: assignments.size,
        confirmedAssignments,
        unconfirmedAssignments: assignments.size - confirmedAssignments,
        pendingSwapRequests: swapIds.size,
        sharedActivityInstances: [...activityMembers.values()].filter(members => members.size >= 2).length,
        sharedWorkgroups: [...workgroupMembers.values()].filter(members => members.size >= 2).length,
    };
});
const canManageSelectedGroup = computed(() =>
    !!selectedGroup.value && (isLocationAdmin.value || selectedGroup.value.is_group_admin)
);
const canDeleteSelectedGroup = computed(() =>
    !!canManageSelectedGroup.value
    && Number(selectedGroup.value?.member_count) === 1
    && groupAdminCount.value === 1
);
const isLastGroupAdmin = (member) =>
    !!member.is_group_admin && groupAdminCount.value === 1;
const groupAdminActionTitle = (member) => {
    if (member.is_group_admin && isLastGroupAdmin(member)) {
        return "Promote another member to group admin before removing this role.";
    }

    const memberName = member.name || "this member";
    return member.is_group_admin
        ? `Remove group-admin status from ${memberName}.`
        : `Make ${memberName} a group admin.`;
};

const canViewMemberScheduling = (member) =>
    Number(member.user_id) === currentUserId
    || isLocationAdmin.value
    || (!!canManageSelectedGroup.value && !!member.allow_admin_management);

const canEditMemberScheduleSettings = (member) =>
    Number(member.user_id) === currentUserId
    || (!!canManageSelectedGroup.value && (isLocationAdmin.value || !!member.allow_admin_management));

let groupSearchTimer;
let availableSearchTimer;
let groupLoadSequence = 0;
let pendingInvitationLoadSequence = 0;
let availableLoadSequence = 0;
let auditLoadSequence = 0;
let adminAuditLoadSequence = 0;
let adminReportLoadSequence = 0;

const loadGroups = async (page = 1) => {
    const sequence = ++groupLoadSequence;
    groupCurrentPage.value = page;
    loading.value = true;
    errorMessage.value = "";
    const requestedLocationId = Number(locationId);
    if (!Number.isSafeInteger(requestedLocationId) || requestedLocationId < 1) {
        groups.value = [];
        isLocationAdmin.value = false;
        allowVolunteerGroupCreation.value = false;
        groupLastPage.value = 1;
        groupTotal.value = 0;
        errorMessage.value = "Select a Location before loading People Groups.";
        loading.value = false;
        return;
    }
    try {
        const params = { location_id: requestedLocationId, page, per_page: 10 };
        if (groupSearch.value.trim()) params.search = groupSearch.value.trim();
        const { data } = await api.get("/people-groups", { params });
        if (sequence !== groupLoadSequence) return;
        isLocationAdmin.value = !!data?.is_location_admin;
        groups.value = data?.data || [];
        groupCurrentPage.value = data?.pagination?.current_page || 1;
        groupLastPage.value = data?.pagination?.last_page || 1;
        groupTotal.value = data?.pagination?.total || 0;
        allowVolunteerGroupCreation.value = !!data?.allow_volunteer_group_creation;
    } catch (error) {
        if (sequence === groupLoadSequence) {
            errorMessage.value = error?.response?.data?.message || "People Groups could not be loaded.";
            console.error("Failed to load People Groups:", error);
        }
    } finally {
        if (sequence === groupLoadSequence) loading.value = false;
    }
};

const loadPendingInvitations = async () => {
    const sequence = ++pendingInvitationLoadSequence;
    if (!canRespondToInvitations.value) {
        pendingInvitations.value = [];
        pendingInvitationsError.value = "";
        return;
    }

    pendingInvitationsLoading.value = true;
    pendingInvitationsError.value = "";
    try {
        const { data } = await api.get("/people-groups/invitations", {
            params: { location_id: Number(locationId) },
        });
        if (sequence === pendingInvitationLoadSequence) {
            pendingInvitations.value = data?.data || [];
        }
    } catch (error) {
        if (sequence === pendingInvitationLoadSequence) {
            pendingInvitationsError.value = error?.response?.data?.message || "Your People Group invitations could not be loaded.";
            console.error("Failed to load pending People Group invitations:", error);
        }
    } finally {
        if (sequence === pendingInvitationLoadSequence) pendingInvitationsLoading.value = false;
    }
};

const scheduleGroupSearch = () => {
    clearTimeout(groupSearchTimer);
    groupSearchTimer = setTimeout(() => loadGroups(1), 250);
};

const loadAvailableMembers = async (page = 1) => {
    if (!selectedGroup.value || !canManageSelectedGroup.value) return;
    const sequence = ++availableLoadSequence;
    const search = memberSearch.value.trim();
    if (!isLocationAdmin.value && search.length < 2) {
        availableMembers.value = [];
        availablePage.value = 1;
        availableLastPage.value = 1;
        availableTotal.value = 0;
        loadingAvailableMembers.value = false;
        return;
    }

    loadingAvailableMembers.value = true;
    errorMessage.value = "";
    try {
        const params = { page, per_page: 10 };
        if (search) params.search = search;
        const { data } = await api.get(`/people-groups/${selectedGroup.value.id}/available-members`, { params });
        if (sequence !== availableLoadSequence) return;
        availableMembers.value = data?.data || [];
        availablePage.value = data?.pagination?.current_page || 1;
        availableLastPage.value = data?.pagination?.last_page || 1;
        availableTotal.value = data?.pagination?.total || 0;
    } catch (error) {
        if (sequence === availableLoadSequence) {
            errorMessage.value = error?.response?.data?.message || "Location members could not be searched.";
        }
    } finally {
        if (sequence === availableLoadSequence) loadingAvailableMembers.value = false;
    }
};

const scheduleAvailableSearch = () => {
    clearTimeout(availableSearchTimer);
    availableSearchTimer = setTimeout(() => loadAvailableMembers(1), 250);
};

const loadAuditLogs = async (page = 1) => {
    if (!selectedGroup.value || !canManageSelectedGroup.value) return;

    const sequence = ++auditLoadSequence;
    const groupId = selectedGroup.value.id;
    auditPage.value = page;
    auditLoading.value = true;
    auditError.value = "";

    try {
        const { data } = await api.get(`/people-groups/${groupId}/audit-logs`, {
            params: { page, per_page: 10 },
        });
        if (sequence !== auditLoadSequence || selectedGroup.value?.id !== groupId) return;
        auditLogs.value = data?.data || [];
        auditLoaded.value = true;
        auditPage.value = data?.pagination?.current_page || page;
        auditLastPage.value = data?.pagination?.last_page || 1;
        auditTotal.value = data?.pagination?.total || 0;
    } catch (error) {
        if (sequence === auditLoadSequence && selectedGroup.value?.id === groupId) {
            auditError.value = error?.response?.data?.message || "Audit history could not be loaded.";
            console.error("Failed to load People Group audit history:", error);
        }
    } finally {
        if (sequence === auditLoadSequence) auditLoading.value = false;
    }
};

const loadAdminReport = async () => {
    if (!props.showAdminReport || !isLocationAdmin.value) return;

    const sequence = ++adminReportLoadSequence;
    adminReportLoading.value = true;
    adminReportError.value = "";
    try {
        const { data } = await api.get("/people-groups/admin/report", {
            params: { location_id: locationId },
        });
        if (sequence === adminReportLoadSequence) adminReport.value = data?.data || null;
    } catch (error) {
        if (sequence === adminReportLoadSequence) {
            adminReportError.value = error?.response?.data?.message || "The People Groups report could not be loaded.";
            console.error("Failed to load People Groups report:", error);
        }
    } finally {
        if (sequence === adminReportLoadSequence) adminReportLoading.value = false;
    }
};

const loadAdminAuditLogs = async (page = 1) => {
    if (!props.showAdminReport || !isLocationAdmin.value) return;

    const sequence = ++adminAuditLoadSequence;
    adminAuditPage.value = page;
    adminAuditLoading.value = true;
    adminAuditError.value = "";
    try {
        const { data } = await api.get("/people-groups/admin/audit-logs", {
            params: { location_id: locationId, page, per_page: 10 },
        });
        if (sequence !== adminAuditLoadSequence) return;
        adminAuditLogs.value = data?.data || [];
        adminAuditLoaded.value = true;
        adminAuditPage.value = data?.pagination?.current_page || page;
        adminAuditLastPage.value = data?.pagination?.last_page || 1;
        adminAuditTotal.value = data?.pagination?.total || 0;
    } catch (error) {
        if (sequence === adminAuditLoadSequence) {
            adminAuditError.value = error?.response?.data?.message || "Location People Group audit events could not be loaded.";
            console.error("Failed to load Location People Group audit events:", error);
        }
    } finally {
        if (sequence === adminAuditLoadSequence) adminAuditLoading.value = false;
    }
};

const handleAdminAuditToggle = (event) => {
    adminAuditExpanded.value = event.currentTarget.open;
    if (adminAuditExpanded.value && !adminAuditLoaded.value && !adminAuditLoading.value) {
        loadAdminAuditLogs(1);
    }
};

const handleAuditToggle = (event) => {
    auditExpanded.value = event.currentTarget.open;
    if (auditExpanded.value && !auditLoaded.value && !auditLoading.value) {
        loadAuditLogs(1);
    }
};

watch(isLocationAdmin, (isAdmin) => {
    if (isAdmin && props.showAdminReport) {
        loadAdminReport();
    } else if (!isAdmin) {
        adminReport.value = null;
        adminReportLoading.value = false;
        adminAuditLogs.value = [];
        adminAuditLoaded.value = false;
        adminAuditExpanded.value = false;
        adminAuditLoading.value = false;
        adminAuditLoadSequence++;
        adminReportLoadSequence++;
    }
});

const createGroup = async () => {
    saving.value = true;
    errorMessage.value = "";
    try {
        const { data } = await api.post("/people-groups", {
            location_id: locationId,
            name: newGroupName.value,
            people_group_type: newGroupType.value,
        });
        toast.success("People Group created.");
        newGroupName.value = "";
        showCreateForm.value = false;
        groupSearch.value = "";
        await loadGroups(1);
        await openGroup(data.data);
    } catch (error) {
        errorMessage.value = error?.response?.data?.message || "People Group could not be created.";
    } finally {
        saving.value = false;
    }
};

const openGroup = async (group) => {
    try {
        const { data } = await api.get(`/people-groups/${group.id}`);
        auditLoadSequence++;
        auditLoading.value = false;
        selectedGroup.value = { ...data.data, shared_assignments: data.shared_assignments || [] };
        auditLogs.value = [];
        auditLoaded.value = false;
        auditPage.value = 1;
        auditLastPage.value = 1;
        auditTotal.value = 0;
        auditError.value = "";
        selectedRuleMember.value = null;
        showAddMemberSection.value = false;
        editGroupName.value = data.data.name;
        editGroupType.value = data.data.people_group_type;
        memberSearch.value = "";
        availableMembers.value = [];
        if (auditExpanded.value && canManageSelectedGroup.value) loadAuditLogs(1);
    } catch (error) {
        errorMessage.value = error?.response?.data?.message || "Group details could not be loaded.";
    }
};

const closeGroupModal = () => {
    availableLoadSequence++;
    auditLoadSequence++;
    loadingAvailableMembers.value = false;
    auditLoading.value = false;
    auditLogs.value = [];
    auditLoaded.value = false;
    auditExpanded.value = false;
    selectedGroup.value = null;
    selectedRuleMember.value = null;
    selectedProfileMember.value = null;
    selectedScheduleMember.value = null;
    showAddMemberSection.value = false;
    availableMembers.value = [];
    emit("group-closed");
};

const loadMemberSchedulingOverview = async (member, force = false) => {
    if (!selectedGroup.value || !canViewMemberScheduling(member)) return;
    if (member.scheduling_overview_loading || (!force && member.scheduling_overview_loaded)) return;

    const groupId = selectedGroup.value.id;
    const memberId = Number(member.user_id);
    member.scheduling_overview_loading = true;
    member.scheduling_overview_error = "";
    try {
        const { data } = await api.get(`/people-groups/${groupId}/members/${memberId}/scheduling-overview`);
        if (
            selectedGroup.value?.id !== groupId
            || !selectedGroup.value.members?.some(current => current === member)
        ) return;
        member.scheduling_overview = data?.data || null;
        member.scheduling_overview_loaded = true;
    } catch (error) {
        if (selectedGroup.value?.id === groupId) {
            member.scheduling_overview_error = error?.response?.data?.message || "Scheduling details could not be loaded.";
            console.error("Failed to load member scheduling overview:", error);
        }
    } finally {
        member.scheduling_overview_loading = false;
    }
};

const handleMemberOverviewToggle = (member, event) => {
    if (event.currentTarget.open) loadMemberSchedulingOverview(member);
};

const refreshAdminAuditIfOpen = () => {
    if (adminAuditExpanded.value && props.showAdminReport && isLocationAdmin.value) {
        loadAdminAuditLogs(adminAuditPage.value);
    }
};

const editMemberProfile = (member, tab = "user-details") => {
    if (tab === "user-details" && !isLocationAdmin.value) return;
    if (tab !== "user-details" && !canEditMemberScheduleSettings(member)) return;
    selectedProfileMember.value = member;
    selectedProfileTab.value = tab;
};

const closeMemberProfile = async () => {
    const member = selectedProfileMember.value;
    selectedProfileMember.value = null;
    if (member && selectedGroup.value) await loadMemberSchedulingOverview(member, true);
    refreshAdminAuditIfOpen();
};

const handleMemberProfileUpdated = async (result) => {
    if (!result?.shouldLeaveEditor || !selectedGroup.value) return;
    const groupId = selectedGroup.value.id;
    selectedProfileMember.value = null;
    await openGroup({ id: groupId });
    refreshAdminAuditIfOpen();
};

const toggleAddMemberSection = async () => {
    showAddMemberSection.value = !showAddMemberSection.value;
    if (showAddMemberSection.value && availableMembers.value.length === 0) {
        await loadAvailableMembers(1);
    }
};

const memberScheduleUrl = (member) => {
    const params = new URLSearchParams({
        personid: String(member.user_id),
        locationid: String(locationId),
        people_group_id: String(selectedGroup.value.id),
        show_chart: "0",
    });
    return `${baseUrl}/MF/cs-mf-personschedule/?${params.toString()}`;
};

const openMemberSchedule = async (member) => {
    try {
        const { data } = await api.get(`/people-groups/${selectedGroup.value.id}/members/${member.user_id}/schedule-access`);
        if (!data?.can_view) {
            toast.error("You do not have permission to view this member's schedule.");
            return;
        }
        selectedScheduleMember.value = member;
    } catch (error) {
        toast.error(error?.response?.data?.message || "This member's schedule could not be opened.");
    }
};

const closeMemberSchedule = () => {
    selectedScheduleMember.value = null;
};

const canRespondForMember = (member) =>
    !!canManageSelectedGroup.value && (isLocationAdmin.value || member.allow_admin_management);

const canManageScheduleForMember = (member) =>
    !!canManageSelectedGroup.value
    && member.user_id !== currentUserId
    && (isLocationAdmin.value || member.allow_admin_management);

const respondToAssignment = async (member, assignment, statusId) => {
    if (!canManageScheduleForMember(member)) return;
    if (statusId === 2 && !window.confirm(`Are you sure you want to decline the assignment on behalf of ${member.name}?`)) return;

    saving.value = true;
    errorMessage.value = "";
    try {
        const groupId = selectedGroup.value.id;
        const { data } = await api.post("/personscheduleajax", {
            cmd: "updatestatus",
            aworkgroupinstanceid: assignment.ai_workgroup_id,
            statusid: statusId,
            personid: member.user_id,
            people_group_id: groupId,
        });
        const responseMessage = data?.message || "";
        if (
            data?.error
            || (data?.returncode !== undefined && Number(data.returncode) !== 0)
            || /^(Unable to update|You are no longer able)/i.test(responseMessage)
        ) {
            throw new Error(responseMessage || "The assignment response could not be saved.");
        }

        toast.success(statusId === 1 ? "Assignment confirmed on behalf of the member." : "Assignment declined on behalf of the member.");
        await loadGroups(groupCurrentPage.value);
        await openGroup({ id: groupId });
    } catch (error) {
        const message = error?.response?.data?.message || error?.message || "The assignment response could not be saved.";
        errorMessage.value = message;
        toast.error(message);
    } finally {
        saving.value = false;
    }
};

const respondToSwap = async (member, swap, response) => {
    const action = response === 1 ? "accept" : "decline";
    if (!window.confirm(`Are you sure you want to ${action} this swap request on behalf of ${member.name}?`)) return;

    saving.value = true;
    errorMessage.value = "";
    try {
        const groupId = selectedGroup.value.id;
        await api.post(`/people-groups/${groupId}/members/${member.user_id}/swaps/${swap.swap_id}/respond`, { response });
        toast.success(`Swap request ${response === 1 ? "accepted" : "declined"}.`);
        await loadGroups(groupCurrentPage.value);
        await openGroup({ id: groupId });
    } catch (error) {
        errorMessage.value = error?.response?.data?.message || "The swap response could not be saved.";
    } finally {
        saving.value = false;
    }
};

const inviteExistingMember = async (member) => {
    if (!selectedGroup.value || !canManageSelectedGroup.value) return;
    const userId = Number(member.user_id);
    if (!Number.isSafeInteger(userId) || userId < 1 || !member.can_invite) {
        errorMessage.value = `${member.name} does not have a valid email address for an invitation.`;
        return;
    }
    saving.value = true;
    errorMessage.value = "";
    try {
        const groupId = selectedGroup.value.id;
        const search = memberSearch.value;
        await api.post(`/people-groups/${groupId}/invitations`, { user_id: userId });
        toast.success(`Invitation sent to ${member.name}. They must accept it to join the group.`);
        await loadGroups(groupCurrentPage.value);
        await openGroup({ id: groupId });
        showAddMemberSection.value = true;
        memberSearch.value = search;
        await loadAvailableMembers(1);
        availableMembers.value = availableMembers.value.filter(candidate => Number(candidate.user_id) !== userId);
    } catch (error) {
        errorMessage.value = error?.response?.data?.message || "The invitation could not be sent.";
    } finally {
        saving.value = false;
    }
};

const cancelInvitation = async (invitation) => {
    if (!selectedGroup.value || !canManageSelectedGroup.value) return;
    const recipientLabel = invitationRecipientLabel(invitation);
    if (!window.confirm(`Cancel the invitation for ${recipientLabel}?`)) return;

    saving.value = true;
    errorMessage.value = "";
    try {
        const groupId = selectedGroup.value.id;
        const showInvitations = showAddMemberSection.value;
        const search = memberSearch.value;
        const availablePageToRestore = availablePage.value;
        await api.delete(`/people-groups/${groupId}/invitations/${invitation.id}`);
        toast.success(`Invitation for ${recipientLabel} cancelled.`);
        await openGroup({ id: groupId });
        showAddMemberSection.value = showInvitations;
        memberSearch.value = search;
        if (showInvitations) await loadAvailableMembers(availablePageToRestore);
    } catch (error) {
        errorMessage.value = error?.response?.data?.message || "The invitation could not be cancelled.";
    } finally {
        saving.value = false;
    }
};

const respondToInvitation = async (invitation, decision) => {
    if (!canRespondToInvitations.value || respondingInvitationId.value !== null) return;
    if (decision === "decline" && !window.confirm(`Decline the invitation to join ${invitation.group.name}?`)) return;

    respondingInvitationId.value = invitation.id;
    invitationActionError.value = "";
    try {
        const payload = { decision };
        if (decision === "accept") {
            payload.allow_admin_management = !!invitationConsent.value[invitation.id];
        }
        await api.post(`/people-groups/invitations/${invitation.id}/respond`, payload);
        delete invitationConsent.value[invitation.id];
        toast.success(decision === "accept"
            ? `You joined ${invitation.group.name}.`
            : `Invitation to ${invitation.group.name} declined.`);
        if (decision === "accept") {
            groupSearch.value = "";
            await loadGroups(1);
        }
        await loadPendingInvitations();
    } catch (error) {
        invitationActionError.value = error?.response?.data?.message || "Your response could not be saved.";
        console.error("Failed to respond to a People Group invitation:", error);
    } finally {
        respondingInvitationId.value = null;
    }
};

const invitationRecipientLabel = (invitation) =>
    invitation.invitee_name || invitation.email || `Invitation #${invitation.id}`;

const editMemberRules = (member) => {
    if (!canEditMemberScheduleSettings(member)) {
        return;
    }
    selectedRuleMember.value = member;
};

const closeMemberRules = async () => {
    const member = selectedRuleMember.value;
    selectedRuleMember.value = null;
    if (member && selectedGroup.value) await loadMemberSchedulingOverview(member, true);
    refreshAdminAuditIfOpen();
};

const saveGroupDetails = async () => {
    saving.value = true;
    errorMessage.value = "";
    try {
        await api.patch(`/people-groups/${selectedGroup.value.id}`, {
            name: editGroupName.value,
            people_group_type: editGroupType.value,
        });
        const groupId = selectedGroup.value.id;
        await loadGroups(groupCurrentPage.value);
        await openGroup({ id: groupId });
        toast.success("People Group updated.");
    } catch (error) {
        errorMessage.value = error?.response?.data?.message || "People Group could not be updated.";
    } finally {
        saving.value = false;
    }
};

const deleteGroup = async () => {
    if (!canDeleteSelectedGroup.value) {
        const message = "Remove all members except one group admin before deleting this group.";
        errorMessage.value = message;
        toast.error(message);
        return;
    }
    if (!window.confirm(`Delete "${selectedGroup.value.name}"?`)) return;
    saving.value = true;
    errorMessage.value = "";
    try {
        await api.delete(`/people-groups/${selectedGroup.value.id}`);
        selectedGroup.value = null;
        await loadGroups(1);
        toast.success("People Group deleted.");
    } catch (error) {
        errorMessage.value = error?.response?.data?.message || "People Group could not be deleted.";
    } finally {
        saving.value = false;
    }
};

const toggleTargetMembership = async (group) => {
    if (!props.targetUserId) return;
    saving.value = true;
    errorMessage.value = "";
    try {
        const isMember = group.member_ids.includes(Number(props.targetUserId));
        if (!isMember) {
            errorMessage.value = "Invite this person by email. They must accept the invitation before joining the group.";
            return;
        }
        await api.delete(`/people-groups/${group.id}/members/${props.targetUserId}`);
        await loadGroups(groupCurrentPage.value);
    } catch (error) {
        const message = error?.response?.data?.message || "Group membership could not be updated.";
        errorMessage.value = message;
        toast.error(message);
    } finally {
        saving.value = false;
    }
};

const updateConsent = async (event) => {
    saving.value = true;
    errorMessage.value = "";
    try {
        await api.patch(`/people-groups/${selectedGroup.value.id}/members/${currentUserId}`, {
            allow_admin_management: event.target.checked,
        });
        await openGroup(selectedGroup.value);
    } catch (error) {
        event.target.checked = !event.target.checked;
        errorMessage.value = error?.response?.data?.message || "Your consent could not be updated.";
    } finally {
        saving.value = false;
    }
};

const toggleGroupAdmin = async (member) => {
    if (!selectedGroup.value || !canManageSelectedGroup.value) return;
    if (member.is_group_admin && isLastGroupAdmin(member)) {
        toast.error("Promote another member to group admin before removing this role.");
        return;
    }

    const promote = !member.is_group_admin;
    const memberName = member.name || `Person #${member.user_id}`;
    const confirmation = promote
        ? `Make ${memberName} a group admin?`
        : `Remove group-admin status from ${memberName}?`;
    if (!window.confirm(confirmation)) return;

    saving.value = true;
    errorMessage.value = "";
    try {
        const groupId = selectedGroup.value.id;
        await api.patch(`/people-groups/${groupId}/members/${member.user_id}`, {
            is_group_admin: promote,
        });
        toast.success(promote ? `${memberName} is now a group admin.` : `${memberName} is no longer a group admin.`);
        await loadGroups(groupCurrentPage.value);
        await openGroup({ id: groupId });
    } catch (error) {
        const message = error?.response?.data?.message || "Group-admin status could not be updated.";
        errorMessage.value = message;
        toast.error(message);
    } finally {
        saving.value = false;
    }
};

const removeGroupMember = async (member) => {
    if (!selectedGroup.value) return;
    const isSelf = Number(member.user_id) === currentUserId;
    if (!isSelf && !canManageSelectedGroup.value) return;
    if (isLastGroupAdmin(member)) {
        const message = "Promote another member to group admin before leaving or removing this member.";
        errorMessage.value = message;
        toast.error(message);
        return;
    }

    const memberName = member.name || `Person #${member.user_id}`;
    const confirmation = isSelf
        ? `Leave "${selectedGroup.value.name}"?`
        : `Remove ${memberName} from "${selectedGroup.value.name}"?`;
    if (!window.confirm(confirmation)) return;

    saving.value = true;
    errorMessage.value = "";
    try {
        const groupId = selectedGroup.value.id;
        await api.delete(`/people-groups/${groupId}/members/${member.user_id}`);
        toast.success(isSelf ? "You left the People Group." : `${memberName} was removed from the People Group.`);
        if (isSelf) closeGroupModal();
        await loadGroups(1);
        if (!isSelf) await openGroup({ id: groupId });
    } catch (error) {
        const message = error?.response?.data?.message || "Group membership could not be updated.";
        errorMessage.value = message;
        toast.error(message);
    } finally {
        saving.value = false;
    }
};
const parseScheduleDate = (value) => {
    if (!value) return null;

    let normalized = value;
    if (typeof value === "string") {
        normalized = value.trim();
        if (/^\d{4}-\d{2}-\d{2}[ T]/.test(normalized) && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized)) {
            normalized = `${normalized.replace(" ", "T")}Z`;
        }
    }

    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
};
const formatDate = (value) => {
    const date = parseScheduleDate(value);
    if (!date) return "Date not available";

    return new Intl.DateTimeFormat(undefined, {
        timeZone: selectedGroup.value?.location_timezone || adminReport.value?.location_timezone || "UTC",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    }).format(date);
};
const formatAuditAction = (action) => (action || "Group activity").replace(/_/g, " ");
const formatAuditDetails = (details) => {
    if (typeof details !== "string") return JSON.stringify(details, null, 2);
    try {
        return JSON.stringify(JSON.parse(details), null, 2);
    } catch {
        return details;
    }
};

watch(canRespondToInvitations, (canRespond) => {
    if (canRespond) {
        loadPendingInvitations();
    } else {
        pendingInvitationLoadSequence++;
        pendingInvitations.value = [];
        pendingInvitationsError.value = "";
    }
});

onMounted(() => {
    const groupsLoaded = loadGroups();
    if (props.initialGroupId) {
        // loadGroups also resolves whether the viewer administers this Location
        groupsLoaded.then(() => openGroup({ id: props.initialGroupId }));
        return;
    }
    loadPendingInvitations();
});
onBeforeUnmount(() => {
    clearTimeout(groupSearchTimer);
    clearTimeout(availableSearchTimer);
    pendingInvitationLoadSequence++;
    auditLoadSequence++;
    adminAuditLoadSequence++;
    adminReportLoadSequence++;
});
</script>

<style scoped>
.people-groups-panel {
    margin: 1.5rem;
    border: 1px solid #dbe3ee;
    border-radius: 0.85rem;
    box-shadow: 0 0.5rem 1.5rem rgb(15 23 42 / 8%);
}

.people-groups-panel > .card-header {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #dbe3ee;
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;
    background: linear-gradient(115deg, #eff6ff, #f8fafc 58%, #f0fdf4);
}

.people-groups-heading {
    display: flex;
    align-items: center;
    gap: 0.85rem;
}

.people-groups-heading__icon {
    display: grid;
    width: 2.85rem;
    height: 2.85rem;
    flex: 0 0 auto;
    place-items: center;
    border-radius: 0.8rem;
    background: #dbeafe;
    color: #1d4ed8;
    font-size: 1.65rem;
}

.people-group-list {
    display: grid;
    gap: 0.75rem;
}

.people-group-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.9rem 1rem;
    border: 1px solid #dbe3ee;
    border-left: 4px solid #60a5fa;
    border-radius: 0.65rem;
    background: linear-gradient(100deg, #f8fbff, #fff 55%);
    box-shadow: 0 0.2rem 0.55rem rgb(15 23 42 / 4%);
    transition: border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease;
}

.people-group-row:hover {
    border-color: #93c5fd;
    box-shadow: 0 0.45rem 0.9rem rgb(15 23 42 / 8%);
    transform: translateY(-1px);
}

.people-group-row__summary {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.75rem;
}

.people-group-row__summary > div {
    min-width: 0;
}

.people-group-row__icon {
    display: grid;
    width: 2.4rem;
    height: 2.4rem;
    flex: 0 0 auto;
    place-items: center;
    border-radius: 50%;
    background: #e0f2fe;
    color: #0369a1;
    font-size: 1.35rem;
}

.people-group-row__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem 0.65rem;
    margin-top: 0.25rem;
    color: #64748b;
    font-size: 0.8rem;
}

.group-type-badge {
    display: inline-flex;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    background: #e0e7ff;
    color: #3730a3;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: capitalize;
}

.pending-group-invitations {
    padding: 1rem;
    border: 1px solid #bfdbfe;
    border-radius: 0.75rem;
    background: linear-gradient(135deg, #eff6ff, #f8fafc 70%);
}

.pending-group-invitations__header {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin-bottom: 0.85rem;
    color: #1e3a8a;
}

.pending-group-invitations__header > .material-icons-round {
    font-size: 1.45rem;
}

.pending-group-invitations__list {
    display: grid;
    gap: 0.65rem;
    margin-top: 0.85rem;
}

.pending-group-invitation {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.85rem;
    border: 1px solid #dbeafe;
    border-radius: 0.55rem;
    background: #fff;
}

.pending-group-invitation__details {
    min-width: 0;
}

.pending-group-invitation__details > .group-type-badge {
    margin-left: 0.45rem;
}

.pending-group-invitation__details > p {
    margin-top: 0.35rem;
}

.invitation-consent-option {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    color: #334155;
    font-size: 0.82rem;
    cursor: pointer;
}

.invitation-consent-option input {
    flex: 0 0 auto;
    margin-top: 0.2rem;
    accent-color: #2563eb;
}

.pending-group-invitation__actions {
    display: flex;
    flex: 0 0 auto;
    gap: 0.5rem;
}

.available-member-list {
    display: grid;
    gap: 0.5rem;
    max-height: 16rem;
    overflow-y: auto;
}

.available-member-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.6rem 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.4rem;
}

.member-detail-row summary {
    cursor: pointer;
    color: #344767;
    font-size: 0.875rem;
}

.group-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1100;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background: rgb(15 23 42 / 55%);
}

.group-modal {
    width: min(680px, 100%);
    max-height: 90vh;
    overflow-y: auto;
    padding: 1.5rem;
    border-radius: 0.75rem;
    background: #fff;
    box-shadow: 0 1rem 3rem rgb(15 23 42 / 25%);
}

.people-group-modal-close {
    flex: 0 0 auto;
    padding: 0 0.25rem;
    border: 0;
    background: transparent;
    color: #344767;
    font-size: 1.75rem;
    font-weight: 400;
    line-height: 1;
    cursor: pointer;
}

.people-group-modal-close:hover {
    color: #172b4d;
}

.people-group-modal-close:focus-visible {
    outline: 2px solid #344767;
    outline-offset: 2px;
}

.group-section {
    padding: 0.9rem;
    border: 1px solid #dbe3ee;
    border-radius: 0.5rem;
}

.group-section--settings,
.group-section--members {
    background: #f8fafc;
}

.group-section--consent,
.group-section--rules {
    background: #f1f5f9;
}

.group-section--add-member,
.group-section--assignments {
    background: #eff6ff;
}

.group-section--swaps {
    background: #fff7ed;
}

.group-section--shared {
    background: #f0fdf4;
}

.group-section--snapshot {
    background: linear-gradient(135deg, #eff6ff, #f8fafc 58%, #f0fdf4);
}

.group-section--audit {
    background: #f5f3ff;
}

.group-section--member {
    background: #fff;
}

.audit-details {
    max-height: 12rem;
    overflow: auto;
    padding: 0.75rem;
    border-radius: 0.375rem;
    background: #fff;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}

.nested-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background: rgb(15 23 42 / 65%);
}

.nested-profile-modal {
    width: min(1100px, 100%);
    max-height: 94vh;
    overflow-y: auto;
    padding: 1.25rem;
    border-radius: 0.75rem;
    background: #fff;
    box-shadow: 0 1rem 3rem rgb(15 23 42 / 35%);
}

.nested-modal-header {
    position: sticky;
    top: 0;
    z-index: 2;
    padding: 0.5rem 0;
    background: #fff;
}

.nested-modal-footer {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: #fff;
}

.rules-modal {
    width: min(1100px, 100%);
}

.schedule-modal-backdrop {
    z-index: 1210;
}

.schedule-modal {
    display: flex;
    flex-direction: column;
    height: 94vh;
}

.schedule-frame {
    flex: 1;
    min-height: 0;
    width: 100%;
    border: 0;
}

.consent-panel {
    padding: 0.9rem;
    border-radius: 0.5rem;
    background: #f8fafc;
}

.group-consent-option {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin: 0;
    cursor: pointer;
}

.group-consent-checkbox {
    position: static;
    flex: 0 0 1rem;
    width: 1rem;
    height: 1rem;
    margin: 0.2rem 0 0;
    padding: 0;
    appearance: auto;
    accent-color: #2dce89;
}

.group-disclosure > summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    cursor: pointer;
    list-style: none;
}

.group-disclosure > summary::-webkit-details-marker {
    display: none;
}

.group-disclosure > summary::after {
    flex: 0 0 auto;
    color: #64748b;
    content: "+";
    font-weight: 700;
}

.group-disclosure[open] > summary::after {
    content: "-";
}

.disclosure-title {
    color: #344767;
    font-weight: 600;
}

.disclosure-description {
    margin-left: auto;
    color: #64748b;
    font-size: 0.8rem;
}

.consent-status {
    margin-left: auto;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    background: #e2e8f0;
    color: #475569;
    font-size: 0.75rem;
    white-space: nowrap;
}

.consent-status--enabled {
    background: #dcfce7;
    color: #166534;
}

.group-stat-grid,
.admin-report-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(135px, 1fr));
    gap: 0.65rem;
}

.group-stat-card,
.admin-report-stat {
    display: flex;
    min-height: 5rem;
    flex-direction: column;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.75rem;
    border: 1px solid #dbe3ee;
    border-radius: 0.55rem;
    background: rgb(255 255 255 / 90%);
}

.group-stat-card span,
.admin-report-stat span {
    color: #64748b;
    font-size: 0.78rem;
    line-height: 1.25;
}

.group-stat-card strong,
.admin-report-stat strong {
    color: #1e293b;
    font-size: 1.45rem;
    line-height: 1;
}

.group-stat-card--confirmed strong {
    color: #15803d;
}

.group-stat-card--pending strong {
    color: #b45309;
}

.group-stat-card--swaps strong {
    color: #c2410c;
}

.group-stat-card--shared strong {
    color: #0369a1;
}

.admin-people-groups-report {
    padding: 1rem;
    border: 1px solid #dbe3ee;
    border-radius: 0.65rem;
    background: #f8fafc;
}

.admin-report-stat {
    min-height: 4.5rem;
}

.admin-global-audit {
    background: #f5f3ff;
}

.member-overview-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
    gap: 0.65rem;
}

.member-overview-card {
    min-width: 0;
    padding: 0.75rem;
    border: 1px solid #dbe3ee;
    border-radius: 0.45rem;
    background: #fff;
}

.member-overview-card h6 {
    margin-bottom: 0.45rem;
    color: #344767;
    font-size: 0.85rem;
}

.member-overview-card ul {
    margin: 0;
    padding-left: 1.1rem;
    color: #475569;
    font-size: 0.8rem;
}

.member-consent-notice {
    padding: 0.6rem 0.75rem;
    border-radius: 0.4rem;
    background: #fff7ed;
    color: #9a3412;
}

@media (max-width: 575px) {
    .people-groups-panel {
        margin: 1rem 0.5rem;
    }

    .people-group-row {
        align-items: flex-start;
        flex-direction: column;
    }

    .pending-group-invitation {
        align-items: stretch;
        flex-direction: column;
    }

    .pending-group-invitation__actions {
        justify-content: flex-end;
    }

    .disclosure-description {
        display: none;
    }

    .group-stat-grid,
    .admin-report-stats {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
